import { fetchApi } from "@/lib/api-helpers";
import { z } from "zod";
import {
  shippingAddressSchema,
  AddressTranslations,
  AddressField,
} from "./addresses.api";
import {
  createPaymentMethodSchema,
  PaymentMethodTranslations,
} from "./payments.api";
import { BrowserInfo } from "@/lib/browser-info";

export interface CheckoutTranslations {
  invalidEmail?: string;
  phoneRequired?: string;
  shippingMethodRequired?: string;
  paymentMethod?: PaymentMethodTranslations;
  shippingAddress?: AddressTranslations;
}

export const inputCheckoutSchema = (
  requirePhone: boolean,
  addressFields: AddressField[],
  requireShippingMethod: boolean,
  requirePaymentMethod: boolean,
  requiredAgreements: boolean,
  translations?: CheckoutTranslations,
  isDigitalOnly?: boolean,
  billingAddressFields?: AddressField[],
) =>
  z.object({
    customer: z.object({
      contact_info: z.object({
        email: z.string().email(translations?.invalidEmail ?? "Invalid email"),
        subscribe_to_news: z.boolean(),
        phone: requirePhone
          ? z
              .string()
              .min(1, translations?.phoneRequired ?? "Phone number is required")
          : z.string().optional(),
      }),
      shipping_address: isDigitalOnly
        ? z.object({
            name: z.string().optional(),
            address1: z.string().optional(),
            address2: z.string().optional(),
            city: z.string().optional(),
            state: z.string().optional(),
            postal_code: z.string().optional(),
            phone: z.string().optional(),
            country: z.string().optional(),
            country_code: z.string().optional(),
          })
        : shippingAddressSchema(addressFields, translations?.shippingAddress),
      ...(requireShippingMethod && {
        shipping_method: z.object({
          id: z
            .string()
            .min(
              1,
              translations?.shippingMethodRequired ??
                "Shipping method is required",
            ),
        }),
      }),
      ...(requirePaymentMethod && {
        payment_method: createPaymentMethodSchema(
          translations?.paymentMethod,
          isDigitalOnly,
          billingAddressFields,
        ),
      }),
      remember_me: z.boolean(),
      agreements_accepted: requiredAgreements ? z.literal(true) : z.boolean(),
    }),
  });

export type CheckoutInput = z.infer<ReturnType<typeof inputCheckoutSchema>>;

// exported for e2e fixture schema-guarding; see checkout-3ds.spec.ts
// normally private — do not depend on in production code
export const checkoutResponseSchema = z.object({
  redirect_url: z.string().optional(),

  // VGS 3DS fields
  three_ds_provider: z.literal("vgs").optional(),
  challenge_form: z.string().optional(),
  challenge_url: z.string().optional(),
  transaction_id: z.string().optional(),
  merchant_tx_id: z.string().optional(),

  // BLIK agreement fields
  blik_agreement_pending: z.boolean().optional(),

  status: z.string().optional(),
  meta: z.object({
    shop_url: z.string().optional(),
    request_id: z.string().optional(),
    timestamp: z.string().optional(),
  }),
  enrollment: z
    .object({
      token: z.string(),
      state: z.string(),
      cart_token: z.string(),
    })
    .optional(),
  order: z
    .object({
      order_number: z.string(),
      status: z.string(),
      token: z.string(),
      order_confirmation_url: z.string().nullable(),
    })
    .optional(),
});

export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;

export interface PaymentMethodData {
  device_id?: string;
  blik_verification_code?: string;
}

export interface CheckoutCartOptions {
  paymentUuid?: string;
  browserInfo?: BrowserInfo;
  kountSessionId?: string;
  paymentMethodData?: PaymentMethodData;
  deviceData?: string;
  acceptedAgreementIds?: number[];
}

export async function checkoutCart(
  cartToken: string,
  options: CheckoutCartOptions = {},
) {
  const {
    paymentUuid,
    browserInfo,
    kountSessionId,
    paymentMethodData,
    deviceData,
    acceptedAgreementIds,
  } = options;

  let endpoint = `/carts/${cartToken}/checkout`;
  if (paymentUuid) {
    endpoint += `?payment_uuid=${paymentUuid}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload: any = {};
  if (browserInfo) {
    payload.browser_info = browserInfo;
  }
  if (kountSessionId) {
    payload.kount_session_id = kountSessionId;
  }
  if (paymentMethodData) {
    payload.payment_method_data = paymentMethodData;
  }
  if (deviceData) {
    payload.device_data = deviceData;
  }
  if (acceptedAgreementIds && acceptedAgreementIds.length > 0) {
    payload.accepted_agreement_ids = acceptedAgreementIds;
  }

  return fetchApi({
    endpoint,
    method: "POST",
    ...(Object.keys(payload).length > 0 && { input: payload }),
    outputSchema: checkoutResponseSchema,
  });
}
